import React from 'react';

/**
 * Применяет маску к элементам ноды и к дочерним нодам, если оные имеются
 *
 * Функция рекурсивно обходит дерево React-элементов и применяет переданную
 * функцию маскирования ко всем строковым и числовым значениям.
 *
 * @param children - React-ноды для обработки. Может быть любым валидным React.ReactNode
 * @param maskFunction - Функция для маскирования строковых значений. Принимает строку и возвращает замаскированную строку
 *
 * @returns Обработанные React-ноды с примененной маской или исходные ноды, если они null/undefined
 *
 * @example
 * ```tsx
 * const maskText = (text: string) => text.replace(/\d/g, '*');
 * const maskedChildren = maskChildNodes(<div>Номер: 123456</div>, maskText);
 * // Результат: <div>Номер: ******</div>
 * ```
 *
 * @example
 * ```tsx
 * const hideLetters = (text: string) => text.replace(/[a-zA-Z]/g, 'X');
 * const children = [
 *   <span>Hello</span>,
 *   "World",
 *   42
 * ];
 * const result = maskChildNodes(children, hideLetters);
 * // Результат: [<span>XXXXX</span>, "XXXXX", "XX"]
 * ```
 */
export default function maskChildNodes(children: React.ReactNode, maskFunction: (value: string) => string) {
    if (children === null || typeof children === 'undefined') {
        return children;
    }

    // Handle single primitive values directly
    if (typeof children === 'string' || typeof children === 'number' || typeof children === 'boolean') {
        return processNode(children, maskFunction);
    }

    // Handle single React element
    if (React.isValidElement(children)) {
        return processNode(children, maskFunction);
    }

    // For arrays, use React.Children.map to preserve all values including null, undefined, boolean
    return React.Children.map(children, (childNode) => processNode(childNode, maskFunction));
}

/**
 * Обрабатывает отдельную ноду, применяя функцию маскирования
 *
 * @param node - Нода для обработки
 * @param maskFunction - Функция для маскирования строковых значений
 * @returns Обработанная нода с примененной маской
 */
function processNode(node: React.ReactNode, maskFunction: (value: string) => string): React.ReactNode {
    // Preserve null, undefined, and boolean values as-is
    if (node === null || typeof node === 'undefined' || typeof node === 'boolean') {
        return node;
    }

    // Если нода является строкой, применяем функцию маскирования
    if (typeof node === 'string') {
        return maskFunction(node);
    }

    // Если нода является числом, конвертируем в строку и применяем маскирование
    if (typeof node === 'number') {
        return maskFunction(node.toString());
    }

    // Если нода является React-элементом, клонируем его и рекурсивно обрабатываем детей
    if (React.isValidElement(node)) {
        return React.cloneElement(node, {
            ...node.props,
            children: maskChildNodes(node.props.children, maskFunction),
        });
    }

    // Для всех остальных типов нод возвращаем как есть
    return node;
}